/* eslint-disable restrict-imports/restrict-imports */
import { useState, useEffect, useCallback } from 'react';
import { SelectOption } from 'react-scale/corp/components/UI/Select';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { FindProjectJSON } from '@scale/shared/types/api/corp-api/find';
import { twMerge } from 'tailwind-merge';
import { Autocomplete } from 'react-scale/components/design-system/product';
import { LargeChip } from 'react-scale/dashboard.scale.com/components/AVTaxonomy/components/common/LargeChip';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from 'design-system';
import { useDebounce } from 'react-use-v13';
import _ from 'lodash';
import { InputLabel } from '@scale/scaleui';

interface SelectProjectsProps {
  customer?: string;
  taskType?: string;
  disallowedTaskTypes?: string[];
  includeTestProjects?: boolean;
  selectedValues: SelectOption[];
  placeholder?: string;
  setSelectedValues: (value: SelectOption[]) => void;
  title?: string;
  description?: string;
  disabled?: boolean;
}

const localStorageDefined = () => typeof localStorage !== 'undefined';

const SelectProjects: React.FC<SelectProjectsProps> = ({
  customer,
  taskType,
  disallowedTaskTypes,
  includeTestProjects,
  selectedValues,
  setSelectedValues,
  placeholder,
  title,
  description,
  disabled,
}: SelectProjectsProps) => {
  const classes = useStyles();
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [searchInput, setSearchInput] = useState<string>('');
  // debounce to not trigger API calls on every keystroke
  useDebounce(
    () => {
      const fetchProjectsAsync = async () => {
        const { options } = await fetchProjects(searchInput);
        setOptions(options);
      };
      fetchProjectsAsync();
    },
    300,
    [searchInput],
  );

  const [recentProjects, setRecentProjects] = useState<SelectOption[]>([]);

  const getRecentProjects = () => {
    const recentProjects = JSON.parse(localStorage.getItem('recentProjects') || '[]');
    return recentProjects;
  };

  const storeRecentProject = useCallback(
    (selectedProjects: SelectOption[]) => {
      if (!localStorageDefined() || !selectedProjects || !selectedProjects.length) return;

      // there should just be one new project here
      const mostRecentProjects = _.difference(selectedValues, selectedProjects);
      const newRecentProjects = [...mostRecentProjects, ...recentProjects].slice(0, 8);

      localStorage.setItem('recentProjects', JSON.stringify(newRecentProjects));
      setRecentProjects(newRecentProjects);
    },
    [recentProjects, selectedValues],
  );

  const getProjectResults = async (search: string): Promise<FindProjectJSON[]> => {
    const query = Object.assign(
      {
        includeTestProjects,
        customer,
        search,
      },
      taskType ? { taskType } : { disallowedTaskTypes },
    );
    const result = await apiFetch(null, 'corp-api/find/projects', { query });
    return result.projects as FindProjectJSON[];
  };

  const fetchProjects = async (search: string) => {
    if (!search && (!customer || !taskType)) {
      return { options: recentProjects };
    }

    try {
      const results = await getProjectResults(search);
      const options =
        results.map(project => {
          const value = project.id;
          const label = project.name;
          return { label, value };
        }) ?? [];

      return { options };
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      return { options: [] };
    }
  };

  useEffect(() => {
    const recentProjects = getRecentProjects();
    setRecentProjects(recentProjects);
  }, []);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex justify-between text-sm items-center">
        {title && <InputLabel labelDescription={description}>{title}</InputLabel>}
        {selectedValues?.length > 0 && !disabled && (
          <div
            className={twMerge('cursor-pointer text-primary-400')}
            onClick={() => setSelectedValues([])}
          >
            Clear
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 w-full relative">
        <div className="w-full">
          <Autocomplete
            fullWidth
            multiple
            filterSelectedOptions
            size="small"
            options={options}
            getOptionLabel={(option: SelectOption) => option.label}
            onInputChange={(_event, newInputValue) => {
              setSearchInput(newInputValue);
            }}
            disabled={disabled}
            onChange={(_e, newValue) => {
              const dedupedValues = _.uniqBy(newValue, 'value');
              storeRecentProject(dedupedValues);
              setSelectedValues(dedupedValues);
            }}
            renderTags={() => <></>}
            value={selectedValues}
            renderInput={params => (
              <TextField
                {...params}
                size="small"
                variant="outlined"
                placeholder={placeholder ?? 'Type an option, press enter'}
                InputProps={{
                  ...params.InputProps,
                  classes: {
                    input: classes.resize,
                  },
                }}
              />
            )}
          />
        </div>
      </div>
      <div className="flex items-center gap-2 w-full flex-wrap">
        {selectedValues.map(option => (
          <LargeChip
            key={option.value}
            label={option.label}
            onClick={() => {}}
            onDismiss={() => {
              if (disabled) return;
              setSelectedValues(selectedValues.filter(entry => entry.value !== option.value));
            }}
          />
        ))}
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  resize: {
    fontSize: '14px !important',
    outline: 'none !important',
    boxShadow: 'none !important',
  },
});

export default SelectProjects;
